import { effect, inject, provideAppInitializer } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { provideRouter, Router, Routes } from '@angular/router';
import { LoggingService } from '@nx-vigil/core/web/logging/data-access';
import { distinctUntilChanged, firstValueFrom, map, Observable } from 'rxjs';

interface Features {
  [key: string]: boolean;
}

interface FeatureRoutesOptions {
  getRoutes: (features: Features) => Routes;
  refreshFeatures: () => Promise<void>;
  features$: Observable<Features>;
  isAuthenticated$: Observable<boolean>;
}

export const provideFeatureRoutes = (
  optionsCallback: () => FeatureRoutesOptions,
) => {
  return [
    provideRouter([]),
    provideAppInitializer(async () => {
      const { getRoutes, refreshFeatures, features$, isAuthenticated$ } =
        optionsCallback();

      const logger = inject(LoggingService);
      logger.trace('provideFeatureRoutes Started');

      const loginChanged = toSignal(
        isAuthenticated$.pipe(distinctUntilChanged()),
      );

      const router = inject(Router);

      await new Promise<void>(resolve => {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const _updateRoutesWhenLoginChanges = effect(async () => {
          const change = loginChanged();
          if (change !== undefined) {
            logger.trace('Auth Changed {authorized}', change);

            //ensure it is the observable emitting, not the signal default
            await refreshFeatures();

            const config = await firstValueFrom(features$.pipe(map(getRoutes)));
            if (config) {
              logger.trace('Updating Routes {routes}', config);
              router.resetConfig(config);

              resolve();
            }
          }
        });
      });

      logger.trace('provideFeatureRoutes Ended');
    }),
  ];
};
